import React, { useState } from "react";
import { X, Calendar } from "lucide-react";

/**
 * OnePADialog - Modal dialog for entering payment reference and optional payment date
 * Used when verifying payments for both Karaoke (onePA) and BBQ (UOB Bank Transfer) bookings
 */
const OnePADialog = ({ 
  isOpen, 
  onClose, 
  transactionNumber, 
  setTransactionNumber, 
  onConfirm,
  bookingType = 'karaoke' 
}) => {
  const [paymentDate, setPaymentDate] = useState('');

  if (!isOpen) return null;

  const isBBQ = bookingType === 'bbq';
  const paymentMethod = isBBQ ? 'UOB Bank Transfer' : 'onePA';
  const referenceLabel = isBBQ ? 'Bank Transfer Reference' : 'onePA Transaction Number';

  const handleConfirm = () => {
    onConfirm(paymentDate || null);
  };

  const handleClose = () => {
    setPaymentDate('');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white border-4 border-black shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] p-6 max-w-md w-full">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold">Verify Payment</h3>
          <button 
            onClick={handleClose}
            className="text-gray-500 hover:text-black transition-colors"
            aria-label="Close dialog"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <div className="space-y-4">
          <div className={`${isBBQ ? 'bg-orange-50 border-orange-400' : 'bg-amber-50 border-amber-400'} border-2 p-4 rounded`}>
            <p className={`text-sm ${isBBQ ? 'text-orange-800' : 'text-amber-800'}`}>
              <strong>Important:</strong> Enter the {referenceLabel} to verify this {isBBQ ? 'BBQ' : 'Karaoke'} payment ({paymentMethod}).
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-bold mb-2">
              {referenceLabel} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={transactionNumber}
              onChange={(e) => setTransactionNumber(e.target.value)}
              placeholder={`Enter ${isBBQ ? 'bank transfer reference' : 'transaction number'}`}
              className="w-full p-3 border-2 border-black focus:ring-2 focus:ring-blue-500 focus:outline-none"
              data-testid="onepa-txn-input"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-bold mb-2 flex items-center gap-2">
              <Calendar size={14} />
              Payment Date <span className="text-xs text-gray-500 font-normal">(optional, defaults to today)</span>
            </label>
            <input
              type="date"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              className="w-full p-3 border-2 border-black focus:ring-2 focus:ring-blue-500 focus:outline-none"
              data-testid="payment-date-input"
            />
          </div>
          
          <div className="flex gap-3 pt-4">
            <button
              onClick={handleClose}
              className="flex-1 py-3 px-4 border-2 border-black font-bold hover:bg-gray-100 transition-colors"
              data-testid="verify-cancel-btn"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={!transactionNumber.trim()}
              data-testid="verify-confirm-btn"
              className={`flex-1 py-3 px-4 border-2 border-black font-bold transition-colors ${
                transactionNumber.trim() 
                  ? 'bg-green-500 text-white hover:bg-green-600' 
                  : 'bg-gray-200 text-gray-500 cursor-not-allowed'
              }`}
            >
              Verify Payment
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnePADialog;
